import axios, { AxiosResponse } from 'axios'
import PapaParse from 'papaparse'

import { APP_CONFIG } from 'src/config.app'
import {
    ModuleValidationErrorResponse,
    PreviewTTL,
    SyncForPreviewResponseStatus,
    UNPROCESSABLE_ENTITY,
} from 'src/hooks/usePreview'
import {
    CreateItemPoolResponse,
    GeneratedItemPoolConfig,
    ImportedItemPoolConfig,
    ItemContainerMetadataDTO,
    ItemPoolMetadataDTO,
    ItemPoolType,
    ManualItemPoolConfig,
} from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { ItemPoolGeneratorResponse } from 'src/models/ItemPoolGeneratorResponse'
import { BUCKETSANDCUPSCSVHEADERS } from 'src/pages/module-builder/buckets-and-cups-editor/BucketsAndCupsCSVImporter'
import { ADAxios, ApiActionNames } from 'src/services/AxiosInterceptor'
import { handleAxiosError, ModuleService } from 'src/services/backend/ModuleService'

export enum GetProgressResponseStatus {
    IN_PROGRESS,
    IN_ERROR,
    COMPLETED,
}

export interface GetProgressResponse {
    status: GetProgressResponseStatus
    message: string
}

export class ItemPoolService {
    public static async generateItemPool(
        config: GeneratedItemPoolConfig
    ): Promise<CreateItemPoolResponse> {
        try {
            const { data }: AxiosResponse<ItemPoolGeneratorResponse> = await ADAxios.post(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools`,
                {
                    type: ItemPoolType.GENERATED,
                    generatedItemPoolConfig: config,
                },
                {
                    apiActionName: ApiActionNames.CreateItemPool,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            return data
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async manualItemPool(
        config: ManualItemPoolConfig
    ): Promise<CreateItemPoolResponse> {
        try {
            const { data }: AxiosResponse<ItemPoolGeneratorResponse> = await ADAxios.post(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools`,
                {
                    type: ItemPoolType.MANUAL,
                    manualItemPoolConfig: config,
                },
                {
                    apiActionName: ApiActionNames.CreateItemPool,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            return data
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async importedItemPool(
        config: ImportedItemPoolConfig
    ): Promise<CreateItemPoolResponse> {
        try {
            const { data }: AxiosResponse<ItemPoolGeneratorResponse> = await ADAxios.post(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools`,
                {
                    type: ItemPoolType.IMPORTED,
                    importedItemPoolConfig: config,
                },
                {
                    apiActionName: ApiActionNames.CreateItemPool,
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            )

            return data
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async buildImportedModulePreview(
        templateModuleVersionId: string,
        row: Record<string, string>
    ): Promise<string> {
        const { data }: AxiosResponse<{ moduleDtoJson: string }> = await ADAxios.post(
            `${APP_CONFIG.backendAPIBaseUrl}/imported-modules/preview`,
            { templateModuleVersionId, row },
            {
                apiActionName: ApiActionNames.CreateItemPool,
                headers: { 'Content-Type': 'application/json' },
            }
        )
        return data.moduleDtoJson
    }

    /**
     * Preview a serialized module template: sync it for preview, poll until published, then launch
     * and return the preview URL. Mirrors the flow in usePreview but works off a raw module JSON.
     */
    public static async previewModuleTemplate(
        moduleTemplateVersionId: string,
        moduleTemplateJson: string
    ): Promise<string> {
        try {
            await ADAxios.post(
                `${APP_CONFIG.backendAPIBaseUrl}/modules-versions/${moduleTemplateVersionId}/preview`,
                {
                    moduleTemplateVersionId,
                    retryIfFailed: false,
                    moduleTemplate: moduleTemplateJson,
                },
                { apiActionName: ApiActionNames.InitiatePreview }
            )
        } catch (e) {
            // Surface backend module-validation failures (422) with their specific messages,
            // matching usePreview, instead of letting the caller show a generic axios error.
            if (axios.isAxiosError(e) && e.response?.status === UNPROCESSABLE_ENTITY) {
                const messages = (
                    e.response.data as ModuleValidationErrorResponse
                ).moduleValidationErrorMessages?.map((error) => error.message)
                throw new Error(
                    messages && messages.length > 0
                        ? `Template validation failed: ${messages.join('; ')}`
                        : 'Template validation failed.'
                )
            }
            throw e
        }

        // eslint-disable-next-line no-magic-numbers
        const MAX_ATTEMPTS = 90
        const poll = async (attempt: number): Promise<void> => {
            const { data }: AxiosResponse<{ status: SyncForPreviewResponseStatus }> =
                await ADAxios.get(
                    `${APP_CONFIG.backendAPIV1BaseUrl}/module-instance-previews/synced-module-templates/${moduleTemplateVersionId}/status`,
                    { apiActionName: ApiActionNames.PreviewProgress }
                )
            if (data.status === SyncForPreviewResponseStatus.SUCCESSFULLY_PUBLISHED) {
                return
            }
            if (
                data.status === SyncForPreviewResponseStatus.FAILED_TO_PUBLISH ||
                data.status === SyncForPreviewResponseStatus.TIMED_OUT
            ) {
                throw new Error(`Preview failed to publish: ${data.status}`)
            }
            if (attempt >= MAX_ATTEMPTS) {
                throw new Error('Timed out waiting for preview to publish')
            }
            // eslint-disable-next-line no-magic-numbers
            await new Promise((resolve) => setTimeout(resolve, 1000))
            return poll(attempt + 1)
        }
        await poll(0)

        const { data: launch }: AxiosResponse<{ previewUrl: string }> = await ADAxios.post(
            `${APP_CONFIG.backendAPIV1BaseUrl}/module-instance-previews/module-instances`,
            { moduleTemplateVersionId, ttlInSeconds: PreviewTTL.THIRTY_MINUTES },
            { apiActionName: ApiActionNames.LaunchPreview }
        )
        return launch.previewUrl
    }

    public static async getProgress(versionId: string): Promise<GetProgressResponse> {
        try {
            const { data }: AxiosResponse<GetProgressResponse> = await ADAxios.get(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools/${versionId}`,
                {
                    apiActionName: ApiActionNames.GetItemPool,
                }
            )

            const status: GetProgressResponseStatus = this.getStatus(data)

            return {
                status: status,
                message: data.status.toString(),
            }
        } catch (e) {
            return {
                status: GetProgressResponseStatus.IN_ERROR,
                message: 'An unexpected error',
            }
        }
    }

    public static async getItemPoolMetadata(
        versionId: string
    ): Promise<ItemPoolMetadataDTO | undefined> {
        try {
            const { data }: AxiosResponse<ItemPoolMetadataDTO> = await ADAxios.get(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools/${versionId}`,
                {
                    apiActionName: ApiActionNames.GetItemPool,
                }
            )
            return data
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async getItemPoolItems(
        versionId: string
    ): Promise<ItemContainerMetadataDTO[] | undefined> {
        try {
            const { data }: AxiosResponse<ItemPoolMetadataDTO> = await ADAxios.get(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools/${versionId}`,
                {
                    apiActionName: ApiActionNames.GetItemPool,
                }
            )
            return data.itemContainers
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async exportBucketsAndCupsCSV(itemPoolContainerVersionIds: string[]) {
        const labels = await this.getItemPoolItemLabels(itemPoolContainerVersionIds)
        if (!labels || labels.length < 1) {
            return ''
        }
        const csvFields = [
            BUCKETSANDCUPSCSVHEADERS.BUCKET_RANK,
            BUCKETSANDCUPSCSVHEADERS.BUCKET,
            BUCKETSANDCUPSCSVHEADERS.BUCKET_MAX_FREQUENCY,
            BUCKETSANDCUPSCSVHEADERS.CUP_RANK,
            BUCKETSANDCUPSCSVHEADERS.CUP,
            BUCKETSANDCUPSCSVHEADERS.ITEM_ID,
        ]
        const csvData = labels.map((label) => {
            return ['', '', '', '', '', label]
        })
        const csv = PapaParse.unparse({ fields: csvFields, data: csvData })
        return new Blob([csv], { type: 'text/csv' })
    }
    public static async getItemPoolItemLabels(itemPoolContainerVersionIds: string[]) {
        const itemPoolLabels: string[] = []
        for (const versionId of itemPoolContainerVersionIds) {
            const moduleLabels = await ModuleService.getModuleItemGroupLabels(versionId)
            for (const label of moduleLabels) {
                itemPoolLabels.push(label)
            }
        }
        return itemPoolLabels
    }

    private static getStatus(r: GetProgressResponse): GetProgressResponseStatus {
        if (r.status.toString().toLowerCase() === 'completed') {
            return GetProgressResponseStatus.COMPLETED
        } else {
            return r.status.toString().toLowerCase().indexOf('error') === -1
                ? GetProgressResponseStatus.IN_PROGRESS
                : GetProgressResponseStatus.IN_ERROR
        }
    }

    public static async exportItemPool(versionId: string): Promise<Blob> {
        try {
            const resp: AxiosResponse<Blob> = await ADAxios.get(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools/${versionId}/export`,
                {
                    responseType: 'blob',
                    apiActionName: ApiActionNames.ExportItemPool,
                }
            )

            return new Blob([resp.data], { type: 'application/zip' })
        } catch (e) {
            handleAxiosError(e)
        }
    }

    public static async checkIfItemPoolIsAutoGenerated(
        itemPoolVersionId: string
    ): Promise<boolean> {
        try {
            const response = await ADAxios.get<{ type: ItemPoolType }>(
                `${APP_CONFIG.backendAPIBaseUrl}/item-pools/${itemPoolVersionId}`,
                {
                    apiActionName: ApiActionNames.GetItemPool,
                }
            )
            return response.data.type === ItemPoolType.GENERATED
        } catch (e) {
            console.error('Error checking if item pool is auto-generated:', e)
            return false
        }
    }
}
